import { BingoServiceClient, GroupRelationshipType } from '@policyfly/protobuf/pfbingo'
import { InternalServiceClient } from '@policyfly/protobuf/pfinternal'
import { ReleaseServiceClient } from '@policyfly/protobuf/pfrelease'
import { isJWTValid } from '@policyfly/utils/api'
import { GrpcStatusCode, GrpcWebFetchTransport } from '@protobuf-ts/grpcweb-transport'
import { RpcError, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { useSessionStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  AddUsersToProgramRequest,
  AddUsersToProgramResponse,
  AddUserToGroupRequest,
  AddUserToGroupResponse,
  CreateProgramRequest,
  CreateProgramResponse,
  Group,
  ListEnvironmentsResponse,
  ListGroupsForProgramRequest,
  ListGroupsForProgramResponse,
  ListProgramsForUsersRequest,
  ListProgramsForUsersResponse,
  ListProgramsRequest,
  ListProgramsResponse,
  ListProgramsForEnvironmentRequest,
  ListProgramsForEnvironmentResponse,
  ListUsersRequest,
  ListUsersForGroupRequest,
  ListUsersForGroupResponse,
  ListGroupsResponse,
  ListGroupsForEnvironmentRequest,
  ListGroupsForEnvironmentResponse,
  ListUsersResponse,
  LoginRequest,
  LoginResponse,
  ReadProgramResponse,
  ReadUserMembershipsRequest,
  ReadUserMembershipsResponse,
  ReadUsersRequest,
  ReadUsersResponse,
  RemoveUsersFromProgramRequest,
  RemoveUsersFromProgramResponse,
  RemoveUserFromGroupRequest,
  RemoveUserFromGroupResponse,
  ReadGroupResponse,
  CreateGroupResponse,
  CreateGroupRequest,
  AddGroupToProgramRequest,
  AddGroupToProgramResponse,
  UpdateGroupRequest,
  UpdateGroupResponse,
  RemoveGroupFromProgramRequest,
  RemoveGroupFromProgramResponse,
  ListProgramsForGroupsRequest,
  ListProgramsForGroupsResponse,
  AddGroupToEnvironmentRequest,
  AddGroupToEnvironmentResponse,
  WriteProgramEnvironmentMetadataRequest,
  WriteProgramEnvironmentMetadataResponse,
  ReadProgramEnvironmentMetadataRequest,
  ReadProgramEnvironmentMetadataResponse,
} from '@policyfly/protobuf/pfbingo'
import type {
  ExportPolicyRequest,
  ExportPolicyResponse,
  FetchAgencyDataRequest,
  FetchAgencyDataResponse,
  ImportPolicyRequest,
  ImportPolicyResponse,
  ListAttachmentsRequest,
  ListAttachmentsResponse,
  ListTarballRequest,
  ListTarballResponse,
  PurgeTarballsRequest,
  PurgeTarballsResponse,
  ReadAttachmentRequest,
  ReadAttachmentResponse,
  ReadTarballRequest,
  ReadTarballResponse,
  RegenerateAttachmentRequest,
  RegenerateAttachmentResponse,
  WriteTarballRequest,
  WriteTarballResponse,
} from '@policyfly/protobuf/pfinternal'
import type {
  GetDiffRequest,
  GetDiffResponse,
  GetFileRequest,
  GetFileResponse,
  GetFileTreeRequest,
  GetFileTreeResponse,
  GetHistoryRequest,
  GetHistoryResponse,
  GetMatrixRequest,
  GetMatrixResponse,
  ListProgramsRequest as ListReleaseProgramsRequest,
  ListProgramsResponse as ListReleaseProgramsResponse,
  ListVersionsRequest,
  ListVersionsResponse,
  PinRequest,
  PinResponse,
  UnpinRequest,
  UnpinResponse,
} from '@policyfly/protobuf/pfrelease'
import type { RpcInterceptor } from '@protobuf-ts/runtime-rpc'
import type { DeepReadonly, Ref } from 'vue'

interface CreateInterceptorParams {
  refreshToken: Ref<string | null>
  accessToken: Ref<string | null>
  transportAnonymous: DeepReadonly<GrpcWebFetchTransport>
}

function createAuthRpcInterceptor (params: CreateInterceptorParams): RpcInterceptor {
  async function getToken (): Promise<string> {
    if (isJWTValid(params.accessToken.value)) return params.accessToken.value
    if (!isJWTValid(params.refreshToken.value)) throw new RpcError('Invalid refresh token', GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED])
    try {
      const bingoServiceClient = new BingoServiceClient(params.transportAnonymous)
      const { response } = await bingoServiceClient.refreshToken({
        environmentId: import.meta.env.VITE_ENVIRONMENT_ID,
        refreshToken: params.refreshToken.value,
      })
      const token = response.accessToken
      params.accessToken.value = token
      return token
    } catch (err) {
      console.error(err)
      throw new RpcError('Failed to get new access token', GrpcStatusCode[GrpcStatusCode.UNAUTHENTICATED])
    }
  }
  async function createInterceptorPromise (...args: Parameters<NonNullable<RpcInterceptor['interceptUnary']>>): Promise<UnaryCall> {
    const [next, method, input, options] = args
    const token = await getToken()
    if (!options.meta) {
      options.meta = {}
    }
    options.meta.authorization = token
    return next(method, input, options)
  }
  return {
    interceptUnary (next, method, input, options): UnaryCall {
      const interceptorPromise = createInterceptorPromise(next, method, input, options)
      return new UnaryCall(
        method,
        options.meta ?? {},
        input,
        interceptorPromise.then((p) => p.headers),
        interceptorPromise.then((p) => p.response),
        interceptorPromise.then((p) => p.status),
        interceptorPromise.then((p) => p.trailers),
      )
    },
  }
}

export const useApiStore = defineStore('api', () => {
  const refreshToken = useSessionStorage<string>('refreshToken', null)
  const userId = useSessionStorage<string | null>('userId', null)
  const accessToken = ref<string | null>(null)

  const loggedIn = computed(() => {
    return !!refreshToken.value
  })

  function logout (): void {
    refreshToken.value = null
    userId.value = null
    accessToken.value = null
  }

  const transportAnonymous = new GrpcWebFetchTransport({
    baseUrl: window.location.origin,
    format: 'binary',
    interceptors: [],
  })
  const interceptorParams: CreateInterceptorParams = {
    refreshToken,
    accessToken,
    transportAnonymous,
  }

  const transport = new GrpcWebFetchTransport({
    baseUrl: window.location.origin,
    format: 'binary',
    interceptors: [
      createAuthRpcInterceptor(interceptorParams),
    ],
  })

  const bingoServiceClientAnonymous = new BingoServiceClient(transportAnonymous)
  async function login (request: LoginRequest): Promise<LoginResponse> {
    const { response } = await bingoServiceClientAnonymous.login(request)
    refreshToken.value = response.refreshToken
    userId.value = response.userId
    accessToken.value = response.accessToken
    return response
  }

  const internalServiceClient = new InternalServiceClient(transport)
  async function policyExport (request: ExportPolicyRequest): Promise<ExportPolicyResponse> {
    const { response } = await internalServiceClient.exportPolicy(request)
    return response
  }
  async function policyImport (request: ImportPolicyRequest): Promise<ImportPolicyResponse> {
    const { response } = await internalServiceClient.importPolicy(request)
    return response
  }
  async function listTarballs (request: ListTarballRequest): Promise<ListTarballResponse> {
    const { response } = await internalServiceClient.listTarball(request)
    return response
  }
  async function readTarball (request: ReadTarballRequest): Promise<ReadTarballResponse> {
    const { response } = await internalServiceClient.readTarball(request)
    return response
  }
  async function writeTarball (request: WriteTarballRequest): Promise<WriteTarballResponse> {
    const { response } = await internalServiceClient.writeTarball(request)
    return response
  }
  async function purgeTarballs (request: PurgeTarballsRequest): Promise<PurgeTarballsResponse> {
    const { response } = await internalServiceClient.purgeTarballs(request)
    return response
  }
  async function listAttachments (request: ListAttachmentsRequest): Promise<ListAttachmentsResponse> {
    const { response } = await internalServiceClient.listAttachments(request)
    return response
  }
  async function regenerateAttachment (request: RegenerateAttachmentRequest): Promise<RegenerateAttachmentResponse> {
    const { response } = await internalServiceClient.regenerateAttachment(request)
    return response
  }
  async function readAttachment (request: ReadAttachmentRequest): Promise<ReadAttachmentResponse> {
    const { response } = await internalServiceClient.readAttachment(request)
    return response
  }

  const bingoServiceClient = new BingoServiceClient(transport)
  async function listEnvironments (): Promise<ListEnvironmentsResponse> {
    const { response } = await bingoServiceClient.listEnvironments({})
    return response
  }
  async function listUsers (request: ListUsersRequest): Promise<ListUsersResponse> {
    const { response } = await bingoServiceClient.listUsers(request)
    return response
  }
  async function listUsersForGroup (request: ListUsersForGroupRequest): Promise<ListUsersForGroupResponse> {
    const { response } = await bingoServiceClient.listUsersForGroup(request)
    return response
  }
  async function loadUserDetails (request: ReadUsersRequest): Promise<ReadUsersResponse> {
    const { response } = await bingoServiceClient.readUsers(request)
    return response
  }
  async function loadUserPrograms (request: ListProgramsForUsersRequest): Promise<ListProgramsForUsersResponse> {
    const { response } = await bingoServiceClient.listProgramsForUsers(request)
    return response
  }
  async function loadProgramDetails (id: string): Promise<ReadProgramResponse> {
    const { response } = await bingoServiceClient.readProgram({ id })
    return response
  }
  async function listPrograms (request: ListProgramsRequest): Promise<ListProgramsResponse> {
    const { response } = await bingoServiceClient.listPrograms(request)
    return response
  }
  async function listProgramsForEnvironment (request: ListProgramsForEnvironmentRequest): Promise<ListProgramsForEnvironmentResponse> {
    const { response } = await bingoServiceClient.listProgramsForEnvironment(request)
    return response
  }
  async function createProgram (request: CreateProgramRequest): Promise<CreateProgramResponse> {
    const { response } = await bingoServiceClient.createProgram(request)
    return response
  }
  async function addToProgram (request: AddUsersToProgramRequest): Promise<AddUsersToProgramResponse> {
    const { response } = await bingoServiceClient.addUsersToProgram(request)
    return response
  }
  async function removeFromProgram (request: RemoveUsersFromProgramRequest): Promise<RemoveUsersFromProgramResponse> {
    const { response } = await bingoServiceClient.removeUsersFromProgram(request)
    return response
  }
  /**
   * Gets the program level group for a specified program.
   */
  async function getProgramGroup (request: ListGroupsForProgramRequest): Promise<Group> {
    const { response } = await bingoServiceClient.listGroupsForProgram(request)
    return response
      .groups
      .find((g) => g.relationshipType === GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_OWNER)!
      .group!
  }
  async function listGroupsForProgram (request: ListGroupsForProgramRequest): Promise<ListGroupsForProgramResponse> {
    const { response } = await bingoServiceClient.listGroupsForProgram(request)
    return response
  }
  async function addToGroup (request: AddUserToGroupRequest): Promise<AddUserToGroupResponse> {
    const { response } = await bingoServiceClient.addUserToGroup(request)
    return response
  }
  async function removeUserFromGroup (request: RemoveUserFromGroupRequest): Promise<RemoveUserFromGroupResponse> {
    const { response } = await bingoServiceClient.removeUserFromGroup(request)
    return response
  }
  async function loadUserMemberships (request: ReadUserMembershipsRequest): Promise<ReadUserMembershipsResponse> {
    const { response } = await bingoServiceClient.readUserMemberships(request)
    return response
  }
  async function listGroups (): Promise<ListGroupsResponse> {
    const { response } = await bingoServiceClient.listGroups({})
    return response
  }
  async function loadGroupDetails (id: string): Promise<ReadGroupResponse> {
    const { response } = await bingoServiceClient.readGroup({ id })
    return response
  }
  async function listAgencyData (request: FetchAgencyDataRequest): Promise<FetchAgencyDataResponse> {
    const { response } = await internalServiceClient.fetchAgencyData(request)
    return response
  }
  async function createGroup (request: CreateGroupRequest): Promise<CreateGroupResponse> {
    const { response } = await bingoServiceClient.createGroup(request)
    return response
  }
  async function updateGroup (request: UpdateGroupRequest): Promise<UpdateGroupResponse> {
    const { response } = await bingoServiceClient.updateGroup(request)
    return response
  }
  async function addGroupToProgram (request: AddGroupToProgramRequest): Promise<AddGroupToProgramResponse> {
    const { response } = await bingoServiceClient.addGroupToProgram(request)
    return response
  }
  async function addGroupToEnvironment (request: AddGroupToEnvironmentRequest): Promise<AddGroupToEnvironmentResponse> {
    const { response } = await bingoServiceClient.addGroupToEnvironment(request)
    return response
  }
  async function listGroupsForEnvironment (request: ListGroupsForEnvironmentRequest): Promise<ListGroupsForEnvironmentResponse> {
    const { response } = await bingoServiceClient.listGroupsForEnvironment(request)
    return response
  }
  async function removeGroupFromProgram (request: RemoveGroupFromProgramRequest): Promise<RemoveGroupFromProgramResponse> {
    const { response } = await bingoServiceClient.removeGroupFromProgram(request)
    return response
  }
  async function listProgramsForGroup (request: ListProgramsForGroupsRequest): Promise<ListProgramsForGroupsResponse> {
    const { response } = await bingoServiceClient.listProgramsForGroups(request)
    return response
  }
  async function loadProgramEnvironmentMetadata (request: ReadProgramEnvironmentMetadataRequest): Promise<ReadProgramEnvironmentMetadataResponse> {
    const { response } = await bingoServiceClient.readProgramEnvironmentMetadata(request)
    return response
  }
  async function updateProgramEnvironmentMetadata (request: WriteProgramEnvironmentMetadataRequest): Promise<WriteProgramEnvironmentMetadataResponse> {
    const { response } = await bingoServiceClient.writeProgramEnvironmentMetadata(request)
    return response
  }

  const releaseServiceClient = new ReleaseServiceClient(transport)
  async function listReleasePrograms (request: ListReleaseProgramsRequest): Promise<ListReleaseProgramsResponse> {
    const { response } = await releaseServiceClient.listPrograms(request)
    return response
  }
  async function listVersionsForProgram (request: ListVersionsRequest): Promise<ListVersionsResponse> {
    const { response } = await releaseServiceClient.listVersions(request)
    return response
  }
  async function getDiff (request: GetDiffRequest): Promise<GetDiffResponse> {
    const { response } = await releaseServiceClient.getDiff(request)
    return response
  }
  async function getFileTree (request: GetFileTreeRequest): Promise<GetFileTreeResponse> {
    const { response } = await releaseServiceClient.getFileTree(request)
    return response
  }
  async function getFile (request: GetFileRequest): Promise<GetFileResponse> {
    const { response } = await releaseServiceClient.getFile(request)
    return response
  }
  async function getHistory (request: GetHistoryRequest): Promise<GetHistoryResponse> {
    const { response } = await releaseServiceClient.getHistory(request)
    return response
  }
  async function getMatrix (request: GetMatrixRequest): Promise<GetMatrixResponse> {
    const { response } = await releaseServiceClient.getMatrix(request)
    return response
  }
  async function pinVersion (request: PinRequest): Promise<PinResponse> {
    const { response } = await releaseServiceClient.pin(request)
    return response
  }
  async function unpinVersion (request: UnpinRequest): Promise<UnpinResponse> {
    const { response } = await releaseServiceClient.unpin(request)
    return response
  }

  return {
    userId,
    loggedIn,
    logout,
    login,
    policyExport,
    policyImport,
    listTarballs,
    readTarball,
    writeTarball,
    purgeTarballs,
    listAttachments,
    regenerateAttachment,
    readAttachment,
    listEnvironments,
    listUsers,
    listUsersForGroup,
    loadUserDetails,
    loadUserPrograms,
    loadProgramDetails,
    listPrograms,
    listProgramsForEnvironment,
    createProgram,
    addToProgram,
    removeFromProgram,
    getProgramGroup,
    addToGroup,
    removeUserFromGroup,
    loadUserMemberships,
    listGroups,
    listGroupsForProgram,
    loadGroupDetails,
    createGroup,
    updateGroup,
    addGroupToProgram,
    addGroupToEnvironment,
    listGroupsForEnvironment,
    removeGroupFromProgram,
    listProgramsForGroup,
    listAgencyData,
    loadProgramEnvironmentMetadata,
    updateProgramEnvironmentMetadata,
    // pfrelease
    getDiff,
    getFile,
    getFileTree,
    getHistory,
    getMatrix,
    listReleasePrograms,
    listVersionsForProgram,
    pinVersion,
    unpinVersion,
  }
})
